import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import FilterSelect from '../Common/FilterSelect';
import salesforceFundraisingsService from 'services/salesforceFundraisingsService';

const FILTER_MAX_LIST_HEIGHT = 200;

const BOOLEAN_OPTIONS = [
	{ label: 'Yes', value: 'true' },
	{ label: 'No', value: 'false' },
];

const LP_INITIAL_FILTERS = {
	active: '',
	existing_investor: '',
	stage: '',
	commitment_date_from: '',
	commitment_date_to: '',
	closing_round: '',
};

const ALL_ACTIVE = { label: 'Active: All', value: '' };
const ALL_EXISTING_LP = { label: 'Existing Investor: All', value: '' };
const ALL_STAGES = { label: 'All stages', value: '' };
const ALL_CLOSING_ROUNDS = { label: 'All closing rounds', value: '' };

const activeOptions = [ALL_ACTIVE, ...BOOLEAN_OPTIONS];
const existingLpOptions = [ALL_EXISTING_LP, ...BOOLEAN_OPTIONS];

const LPFundraisingFilters = ({ filters, onChange }) => {
	const [picklistValues, setPicklistValues] = useState(null);
	const picklistFetched = useRef(false);

	useEffect(() => {
		if (picklistFetched.current) return;

		const fetchPicklists = async () => {
			try {
				const data = await salesforceFundraisingsService.getPicklistValues();
				setPicklistValues(data);
				picklistFetched.current = true;
			} catch (err) {
				console.error('Failed to load picklist values:', err);
			}
		};

		fetchPicklists();
	}, []);

	const stageOptions = [ALL_STAGES, ...(picklistValues?.stage || [])];
	const closingRoundOptions = [
		ALL_CLOSING_ROUNDS,
		...(picklistValues?.closing_round || []),
	];

	const handleChange = (key, value) => {
		onChange({ ...filters, [key]: value });
	};

	const handleDateChange = (key, date) => {
		if (date === null) {
			handleChange(key, '');
			return;
		}
		if (!date.isValid()) return;

		if (key === 'commitment_date_from' && filters.commitment_date_to) {
			if (date.isAfter(dayjs(filters.commitment_date_to))) return;
		}
		if (key === 'commitment_date_to' && filters.commitment_date_from) {
			if (date.isBefore(dayjs(filters.commitment_date_from))) return;
		}

		handleChange(key, date.format('YYYY-MM-DD'));
	};

	const hasActiveFilters = Object.values(filters).some((v) => v !== '');

	const handleReset = () => {
		onChange({ ...LP_INITIAL_FILTERS });
	};

	const datePickerSlotProps = (key) => ({
		textField: {
			size: 'small',
			InputProps: { sx: { height: 40, minHeight: 40 } },
			sx: { width: 140 },
			onKeyDown: (e) => {
				if (e.key === 'Enter') {
					handleDateChange(key, dayjs(e.target.value));
				}
			},
			onBlur: (e) => {
				handleDateChange(key, dayjs(e.target.value));
			},
		},
	});

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1,
				padding: '8px 14px',
				borderBottom: '0.5px solid',
				borderColor: 'border.light',
				flexWrap: 'wrap',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 2,
					flex: 1,
					flexWrap: 'wrap',
				}}
			>
				<FilterSelect
					maxListHeight={FILTER_MAX_LIST_HEIGHT}
					width={140}
					height={40}
					value={filters.active}
					options={activeOptions}
					onChange={(val) => handleChange('active', val)}
				/>

				<FilterSelect
					maxListHeight={FILTER_MAX_LIST_HEIGHT}
					width={140}
					height={40}
					value={filters.existing_investor}
					options={existingLpOptions}
					onChange={(val) => handleChange('existing_investor', val)}
				/>

				<FilterSelect
					maxListHeight={FILTER_MAX_LIST_HEIGHT}
					width={140}
					height={40}
					value={filters.stage}
					options={stageOptions}
					onChange={(val) => handleChange('stage', val)}
				/>

				<DatePicker
					label='From'
					value={
						filters.commitment_date_from
							? dayjs(filters.commitment_date_from)
							: null
					}
					maxDate={
						filters.commitment_date_to
							? dayjs(filters.commitment_date_to)
							: null
					}
					onAccept={(date) => handleDateChange('commitment_date_from', date)}
					slotProps={datePickerSlotProps('commitment_date_from')}
				/>

				<DatePicker
					label='To'
					value={
						filters.commitment_date_to
							? dayjs(filters.commitment_date_to)
							: null
					}
					minDate={
						filters.commitment_date_from
							? dayjs(filters.commitment_date_from)
							: null
					}
					onAccept={(date) => handleDateChange('commitment_date_to', date)}
					slotProps={datePickerSlotProps('commitment_date_to')}
				/>

				<FilterSelect
					maxListHeight={FILTER_MAX_LIST_HEIGHT}
					width={140}
					height={40}
					value={filters.closing_round}
					options={closingRoundOptions}
					onChange={(val) => handleChange('closing_round', val)}
				/>
			</Box>

			{hasActiveFilters && (
				<Button
					variant='outlined'
					size='small'
					onClick={handleReset}
					sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}
				>
					Reset filters
				</Button>
			)}
		</Box>
	);
};

LPFundraisingFilters.propTypes = {
	filters: PropTypes.shape({
		active: PropTypes.string.isRequired,
		existing_investor: PropTypes.string.isRequired,
		stage: PropTypes.string.isRequired,
		commitment_date_from: PropTypes.string.isRequired,
		commitment_date_to: PropTypes.string.isRequired,
		closing_round: PropTypes.string.isRequired,
	}).isRequired,
	onChange: PropTypes.func.isRequired,
};

export { LP_INITIAL_FILTERS };
export default LPFundraisingFilters;
